import { makeStyles, shorthands, tokens } from '@fluentui/react-components';

export const useActiveConversationsTableStyles = makeStyles({
    tableContainer: {
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        marginTop: tokens.spacingVerticalM,
        '& [role="gridcell"]': {
            textAlign: 'left',
            justifyContent: 'flex-start',
        },
        '& [role="columnheader"]': {
            textAlign: 'left',
            justifyContent: 'flex-start',
        },
    },
    autoGeneratedRow: {
        borderLeft: `3px solid ${tokens.colorBrandForeground1}`,
        backgroundColor: tokens.colorNeutralBackground2,
        '&:hover': {
            backgroundColor: tokens.colorNeutralBackground2Hover,
        },
    },
    dataGrid: {
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
    },
    tableToolbar: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginBottom: tokens.spacingVerticalS,
    },
    groupedSections: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalM,
    },
    groupSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
    },
    groupHeaderRow: {
        minHeight: '44px',
        width: '100%',
        boxSizing: 'border-box',
        backgroundColor: tokens.colorNeutralBackground2,
        '& [role="gridcell"]': {
            backgroundColor: tokens.colorNeutralBackground2,
        },
    },
    groupHeaderCell: {
        padding: 0,
        overflow: 'hidden',
        maxWidth: '100%',
        width: '100%',
        backgroundColor: tokens.colorNeutralBackground2,
    },
    groupCountCell: {
        justifyContent: 'flex-end',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        backgroundColor: tokens.colorNeutralBackground2,
    },
    groupHeaderSpacerCell: {
        padding: 0,
        overflow: 'hidden',
        backgroundColor: tokens.colorNeutralBackground2,
    },
    groupToggleButton: {
        width: '100%',
        justifyContent: 'space-between',
        padding: 0,
        minHeight: '44px',
        borderRadius: '8px',
        backgroundColor: tokens.colorNeutralBackground2,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        display: 'flex',
        flex: 1,
    },

    groupHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
        width: '100%',
        fontWeight: 600,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        minWidth: 0,
        flex: 1,
    },
    groupMeta: {
        fontSize: '12px',
        fontWeight: 400,
        color: tokens.colorNeutralForeground3,
        whiteSpace: 'nowrap',
        flexShrink: 0,
    },
    tableHeader: {
        minWidth: 0,
    },
    tableBody: {
        minWidth: 0,
    },
    tableHeaderRow: {
        minWidth: 0,
        boxSizing: 'border-box',
        justifyContent: 'space-between',
        height: '44px',
        ...shorthands.padding(0, tokens.spacingHorizontalXL),
        '@media (max-width:640px)': {
            ...shorthands.padding(0, tokens.spacingHorizontalM),
        },
    },
    tableBodyRow: {
        minWidth: 0,
        boxSizing: 'border-box',
        justifyContent: 'space-between',
        height: '44px',
        cursor: 'pointer',
        ...shorthands.padding(0, tokens.spacingHorizontalXL),
        '@media (max-width:640px)': {
            ...shorthands.padding(0, tokens.spacingHorizontalM),
        },
        '&:hover': { backgroundColor: tokens.colorNeutralBackground2Hover },
        '&:focus-visible': {
            outline: `${tokens.strokeWidthThick} solid ${tokens.colorStrokeFocus2}`,
            outlineOffset: '-1px',
        },
    },
    tableSelectionCell: {
        minWidth: '180px',
        maxWidth: 'none',
    },
    cell: {},
    centeredHeaderCell: {
        textAlign: 'center',
    },
    centeredCell: {
        textAlign: 'center',
    },
    actionCell: {
        '&:active': {
            backgroundColor: 'none',
        },
        '&:hover': {
            backgroundColor: 'none',
        },
    },
    selectCellWrapper: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        justifyContent: 'flex-start',
        flexWrap: 'nowrap',
        overflow: 'hidden',
        width: '100%',
    },
    newBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        height: '20px',
        lineHeight: '20px',
        paddingLeft: '10px',
        paddingRight: '10px',
        borderRadius: '999px',
        fontSize: '10px',
        fontWeight: 600,
        letterSpacing: '.5px',
        backgroundColor: tokens.colorBrandBackground,
        color: tokens.colorNeutralForegroundOnBrand,
        userSelect: 'none',
        flexShrink: 0,
        maxWidth: '50px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    autoBadge: {
        flexShrink: 0,
        marginLeft: tokens.spacingHorizontalS,
    },
    newNoteBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        height: '20px',
        lineHeight: '20px',
        paddingLeft: '8px',
        paddingRight: '8px',
        borderRadius: '999px',
        fontSize: '10px',
        fontWeight: 600,
        backgroundColor: tokens.colorBrandBackground,
        color: tokens.colorNeutralForegroundOnBrand,
        userSelect: 'none',
        flexShrink: 0,
        whiteSpace: 'nowrap',
    },
    categoriesCellWrapper: {
        width: '100%',
        minWidth: 0,
        overflow: 'hidden',
    },
    categoriesCell: {
        display: 'block',
        whiteSpace: 'nowrap',
        width: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    awaitingGroundResponse: {
        fontWeight: 'bold',
    },
    arrivingSoonField: {
        color: tokens.colorStatusWarningBackground3,
        fontWeight: 'bold',
    },
    openForHourField: {
        color: tokens.colorStatusDangerBackground3,
        fontWeight: 'bold',
    },
    conversationStatusIndicator: {
        display: 'inline-block',
        height: tokens.spacingHorizontalXS,
        width: tokens.spacingHorizontalXS,
        marginRight: tokens.spacingHorizontalXS,
        ...shorthands.borderRadius('50%'),
    },
    statusWrapper: {
        display: 'flex',
        alignItems: 'center',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        minWidth: 0,
    },
    spinnerWrapper: {
        margin: 0,
        flexShrink: 0,
    },
    checkboxWrapper: {
        margin: 0,
        flexShrink: 0,
    },
    statusIconPaused: {
        marginRight: '4px',
        color: tokens.colorStatusDangerForeground1,
    },
    statusIconActive: {
        marginRight: '4px',
        color: tokens.colorStatusSuccessForeground1,
    },
    eyeIconRead: {
        color: tokens.colorPaletteBlueForeground2,
    },
    eyeIconUnread: {
        opacity: 0.4,
    },
    claimConversationBtn: {
        minWidth: 'auto',
        height: '24px',
        ...shorthands.padding('0', tokens.spacingHorizontalXS),
        fontSize: '12px',
        marginLeft: tokens.spacingHorizontalXS,
        flexShrink: 0,
    },
    notesBtn: {
        minWidth: 'auto',
        height: '24px',
        ...shorthands.padding('0', tokens.spacingHorizontalXS),
        fontSize: '12px',
        marginLeft: tokens.spacingHorizontalXS,
        flexShrink: 0,
        margin: tokens.spacingVerticalS,
    },
});
